import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, publicFileUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy } from "lucide-react";
import GironeBadge from "@/components/GironeBadge";
import { Link, useNavigate } from "react-router-dom";

const SHIELD_PLACEHOLDER = "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=200&q=80";

function useStandings() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/public/standings").then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  return rows;
}

export function GeneralStandings() {
  const rows = useStandings();
  // Classifica Generale: unico criterio è la SOMMA PUNTI TOTALE (fantavoto stagionale).
  // Nessun peso di 3/1/0 né head-to-head: chi ha totalizzato più punti sommando
  // le giornate è primo.
  const sortedByTotal = [...rows].sort(
    (a, b) =>
      ((b.total_points || 0) - (a.total_points || 0)) ||
      (a.team_name || "").localeCompare(b.team_name || "")
  );
  return (
    <StandingsPage title="Classifica Generale" subtitle="Ordinata per somma punti totale (fantavoto stagionale)">
      {sortedByTotal.length === 0
        ? <Empty text="Ancora nessuna squadra approvata dall'Admin." />
        : <StandingsTable rows={sortedByTotal.map((t, i) => ({ ...t, rank: i + 1 }))} variant="general" />}
    </StandingsPage>
  );
}

export function GironiStandings() {
  const rows = useStandings();
  const by = { A: [], B: [], C: [], D: [] };
  rows.forEach((t) => by[t.girone]?.push(t));
  // La classifica gironi mantiene l'ordine calcolato dal backend (che applica già
  // tutti i criteri FIFE: punti → somma punti totale → DR → GF → GS → classifica avulsa)
  return (
    <StandingsPage title="Classifica Gironi">
      <Tabs defaultValue="A">
        <TabsList className="bg-slate-50 border border-slate-200 w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
          {["A", "B", "C", "D"].map((g) => (
            <TabsTrigger key={g} value={g} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white inline-flex items-center gap-1.5 px-2 sm:px-3"
                         data-testid={`standings-tab-${g}`}>
              <GironeBadge girone={g} size={18} />
              <span className="hidden sm:inline">Girone</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {["A", "B", "C", "D"].map((g) => (
          <TabsContent key={g} value={g} className="mt-6">
            {by[g].length === 0
              ? <Empty text={`Nessuna squadra ancora approvata nel Girone ${g}.`} />
              : <StandingsTable rows={by[g].map((t, i) => ({ ...t, rank: i + 1 }))} />}
          </TabsContent>
        ))}
      </Tabs>
    </StandingsPage>
  );
}

function StandingsPage({ title, subtitle, children }) {
  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} /> Classifica live
          </div>
          <h1 className="display font-black text-4xl md:text-5xl mt-1">{title}</h1>
          {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {children}
      </div>
      <Footer />
    </div>
  );
}

/**
 * Tabella classifica FIFE 2026/27:
 * POS · SQUADRA · Girone · PG · V · N · P · GF · GS · DR · TP (fantavoto tot) · PT (punti classifica)
 * variant="general" evidenzia TP come metrica principale (classifica ordinata per fantavoto).
 * variant="gironi" (default) evidenzia PT come metrica principale.
 */
function StandingsTable({ rows, variant = "gironi" }) {
  const navigate = useNavigate();
  const isGeneral = variant === "general";
  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid="standings-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="px-3 py-3 text-left w-12" title="Posizione">POS</th>
              <th className="px-3 py-3 text-left">Squadra</th>
              <th className="px-3 py-3 text-center hidden lg:table-cell w-16" title="Girone">Girone</th>
              <th className="px-3 py-3 text-center w-12" title="Partite giocate">PG</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell" title="Vittorie">V</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell" title="Pareggi">N</th>
              <th className="px-2 py-3 text-center w-10 hidden sm:table-cell" title="Perse">P</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell" title="Gol Fatti">GF</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell" title="Gol Subiti">GS</th>
              <th className="px-2 py-3 text-center w-14 hidden md:table-cell" title="Differenza Reti">DR</th>
              <th className={`px-2 py-3 text-center w-20 ${isGeneral ? "" : "hidden md:table-cell"}`} title="Somma Punti Totale (fantavoto stagionale)">TP</th>
              <th className={`px-3 py-3 text-right w-14 ${isGeneral ? "hidden md:table-cell" : ""}`} title="Punti classifica (3 vittoria / 1 pareggio / 0 sconfitta)">PT</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => {
              const dr = typeof t.dr === "number" ? t.dr : ((t.gf || 0) - (t.gs || 0));
              const go = () => t.user_id && navigate(`/squadra/${t.user_id}`);
              return (
                <tr key={t.user_id}
                    role="button"
                    tabIndex={0}
                    onClick={go}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } }}
                    className="border-t border-slate-100 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none transition-colors fade-up cursor-pointer"
                    style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
                    data-testid={`standings-row-${t.user_id}`}>
                  <td className="px-3 py-3 font-black text-slate-700">
                    <span className="pl-2">{t.rank}</span>
                  </td>
                  <td className="px-3 py-3">
                    <Link to={`/squadra/${t.user_id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-3 hover:text-slate-900" data-testid={`standings-team-link-${t.user_id}`}>
                      <img src={t.shield_path ? publicFileUrl(t.shield_path) : SHIELD_PLACEHOLDER}
                           onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }}
                           alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{t.team_name}</div>
                        {t.coach_name && <div className="text-[11px] text-slate-500 truncate">{t.coach_name}</div>}
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-center hidden lg:table-cell">
                    <div className="inline-flex justify-center"><GironeBadge girone={t.girone} size={22} /></div>
                  </td>
                  <td className="px-3 py-3 text-center text-slate-700 font-bold tabular-nums">{t.played || 0}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-emerald-600 font-bold tabular-nums">{t.wins || 0}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-slate-500 font-bold tabular-nums">{t.draws || 0}</td>
                  <td className="px-2 py-3 text-center hidden sm:table-cell text-rose-500 font-bold tabular-nums">{t.losses || 0}</td>
                  <td className="px-2 py-3 text-center hidden md:table-cell text-slate-700 tabular-nums">{Number(t.gf || 0).toFixed(0)}</td>
                  <td className="px-2 py-3 text-center hidden md:table-cell text-slate-700 tabular-nums">{Number(t.gs || 0).toFixed(0)}</td>
                  <td className={`px-2 py-3 text-center hidden md:table-cell font-bold tabular-nums ${dr > 0 ? "text-emerald-600" : dr < 0 ? "text-rose-500" : "text-slate-500"}`}>
                    {dr > 0 ? "+" : ""}{Number(dr).toFixed(0)}
                  </td>
                  <td className={`px-2 py-3 text-center tabular-nums ${isGeneral ? "font-black text-right" : "hidden md:table-cell text-slate-600"}`}
                      style={isGeneral ? { color: "var(--brand)" } : undefined}
                      data-testid={`standings-total-${t.user_id}`}>
                    {Number(t.total_points || 0).toFixed(1)}
                  </td>
                  <td className={`px-3 py-3 text-right tabular-nums ${isGeneral ? "hidden md:table-cell text-slate-600 font-semibold" : "font-black"}`}
                      style={isGeneral ? undefined : { color: "var(--brand)" }}
                      data-testid={`standings-points-${t.user_id}`}>
                    {Number(t.points || 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="card-surface rounded-2xl p-8 text-center text-slate-500">{text}</div>;
}
